import React, { useState } from 'react';
import styled from 'styled-components';
import TabNavigation from './TabNavigation';
import SummaryTab from './SummaryTab';
import SpecificationTab from './SpecificationTab';
import ReportTab from './ReportTab';
import { useReport } from '../contexts/ReportContext';

const ReportContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: box-shadow ${({ theme }) => theme.transitions.base};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const TabContent = styled.div`
  flex: 1;
  overflow: hidden;
`;

const ReportArea: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'summary' | 'specification' | 'report'>('summary');
  const { reportState, isAutoTriggeringFromUrl, hasChatStarted } = useReport();

  const canShowData = hasChatStarted || isAutoTriggeringFromUrl;

  // Check if report has valid data
  const hasValidReportData = (report: any): boolean => {
    if (!report) return false;
    
    if (typeof report === 'string') {
      return report.trim().length > 0;
    }
    
    if (typeof report === 'object') {
      const keys = Object.keys(report);
      if (keys.length === 0) return false;
      
      // Check if at least 1 field has a valid value
      return keys.some(key => {
        const value = report[key];
        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim().length === 0) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      });
    }
    
    return true;
  };

  const tabs = [
    { 
      id: 'summary' as const, 
      label: 'インシデント概要', 
      hasNewContent: canShowData && !!reportState.overview && reportState.overview.trim().length > 0
    },
    { 
      id: 'specification' as const, 
      label: '仕様確認結果', 
      hasNewContent: canShowData && reportState.specificationAnalysis.length > 0
    },
    { 
      id: 'report' as const, 
      label: 'レポート', 
      hasNewContent: canShowData && hasValidReportData(reportState.report)
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'summary':
        return <SummaryTab />;
      case 'specification':
        return <SpecificationTab />;
      case 'report':
        return <ReportTab />;
      default:
        return <SummaryTab />;
    }
  };

  return (
    <ReportContainer>
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as 'summary' | 'specification' | 'report')}
      />
      <TabContent>
        {renderTabContent()}
      </TabContent>
    </ReportContainer>
  );
};

export default ReportArea;